import React, { useState } from 'react';
import { useMutation } from '@apollo/client';
import { RecordDataExportDocument } from '../../generated/graphql';
import { Dialog } from './Dialog/Dialog';

interface DataExportJustificationDialogProps {
  isOpen: boolean;
  onConfirm: (justification: string) => void;
  onCancel: () => void;
  exportType: string;
  columns: string[];
  recordCount: number;
  filters?: string;
  confirmLabel?: string;
}

export const DataExportJustificationDialog: React.FC<DataExportJustificationDialogProps> = ({
  isOpen,
  onConfirm,
  onCancel,
  exportType,
  columns,
  recordCount,
  filters,
  confirmLabel = 'Export',
}) => {
  const [justification, setJustification] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [recordExport, { loading }] = useMutation(RecordDataExportDocument);

  const isValid = justification.trim().length >= 10;

  const handleConfirm = async () => {
    setError(null);
    try {
      const result = await recordExport({
        variables: {
          input: {
            exportType,
            columns,
            filters: filters || null,
            justification: justification.trim(),
            recordCount,
          },
        },
      });

      if (result.data?.recordDataExport?.success) {
        onConfirm(justification.trim());
        setJustification('');
      } else {
        setError(result.data?.recordDataExport?.error || 'Failed to record export');
      }
    } catch {
      setError('Failed to record export audit. Please try again.');
    }
  };

  const handleCancel = () => {
    setJustification('');
    setError(null);
    onCancel();
  };

  return (
    <Dialog isOpen={isOpen} onClose={handleCancel} maxWidth="md">
      <div className="px-6 py-6" data-testid="data-export-justification-dialog">
        <h3 className="text-lg font-semibold text-gray-900 mb-2">
          Data Export Justification
        </h3>
        <p className="text-sm text-gray-600 mb-4">
          This export contains personal data and requires a justification for GDPR compliance.
        </p>

        {/* Export details */}
        <div className="bg-gray-50 rounded-md p-3 mb-4 text-sm">
          <div className="flex justify-between">
            <span className="text-gray-500">Export type:</span>
            <span className="text-gray-900 font-medium">{exportType}</span>
          </div>
          {recordCount > 0 && (
            <div className="flex justify-between mt-1">
              <span className="text-gray-500">Records:</span>
              <span className="text-gray-900 font-medium">{recordCount.toLocaleString()}</span>
            </div>
          )}
        </div>

        {/* Justification textarea */}
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Justification <span className="text-red-500">*</span>
        </label>
        <textarea
          data-testid="export-justification-textarea"
          value={justification}
          onChange={(e) => setJustification(e.target.value)}
          placeholder="Why do you need to export this data? (min 10 characters)"
          className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
          rows={3}
          disabled={loading}
        />
        {justification.length > 0 && justification.trim().length < 10 && (
          <p className="text-xs text-red-500 mt-1">
            Justification must be at least 10 characters
          </p>
        )}

        {/* Error message */}
        {error && (
          <p data-testid="export-justification-error" className="text-sm text-red-600 mt-2">
            {error}
          </p>
        )}

        {/* Actions */}
        <div className="flex justify-end gap-3 mt-4">
          <button
            data-testid="export-justification-cancel"
            onClick={handleCancel}
            disabled={loading}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            data-testid="export-justification-confirm"
            onClick={handleConfirm}
            disabled={!isValid || loading}
            className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? 'Recording...' : confirmLabel}
          </button>
        </div>
      </div>
    </Dialog>
  );
};
